import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

export const DragHandle = ({ dragRef, sortHandleClass = '' }) => {
  return (
    <div ref={dragRef} className={`sort-handle ${sortHandleClass}`}>
      <div className="up"></div>
      <div className="handle icon-grid"></div>
      <div className="down"></div>
    </div>
  );
};

// How deep into the element the cursor needs to go before swapping, Default 50%
const DEFAULT_HOVER_THRESHOLD = 0.5;

export const DropArea = ({ accept, item, onDrop = () => {}, onMove, render, hoverThreshold = DEFAULT_HOVER_THRESHOLD, invisiblePreview = false, monitorProps = false, mutateIndex = true, checkBounds = true }) => {
  const dropRef = React.useRef();
  const dragRef = React.useRef();

  const [, drop] = useDrop({
    accept,
    drop: (dropItem, monitor) => monitorProps ? onDrop(item, monitor) : onDrop(item),
    hover(dragItem, monitor) {
      if (!dropRef.current) {
        return;
      }

      const hoverIndex = item.index;
      const dragIndex = dragItem.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Drag behavior will be determine in component
      if (monitorProps){
        onMove(dragIndex, hoverIndex, monitor);
        return;
      }
      if (checkBounds) {
        // Determine rectangle on screen
        const hoverBoundingRect = dropRef.current?.getBoundingClientRect();
        // Get vertical middle
        const hoverThresholdY = (hoverBoundingRect.bottom - hoverBoundingRect.top) * (1 - hoverThreshold);
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Dragging downwards, Get pixels from the bottom
        if (dragIndex < hoverIndex && hoverBoundingRect.bottom - clientOffset.y < hoverThresholdY) {
          return;
        }
        // Dragging upwards, Get pixels to the top
        if (dragIndex > hoverIndex && clientOffset.y - hoverBoundingRect.top > hoverThresholdY) {
          return;
        }
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      if (mutateIndex) {
        dragItem.index = hoverIndex;
      }
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(dropRef));
  if (invisiblePreview) {
    preview(getEmptyImage(), {captureDraggingState: true});
  }

  drag(dragRef);

  if (render) {
    return render({ dropRef, dragRef, isDragging });
  }
};
